import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBA, ZUST_SBB, ZUST_SBD, ZUST_SBS } from "../../../consts/funktionaleRollen";
import { getAnlagenDateienEndpoint, getAnlagenDateiUploadEndpoint } from "../../../services/AnlagenService";
import {
    getBescheidEndpoint,
    getBescheidsDateienEndpoint,
    getBescheidsDateiUploadEndpoint
} from "../../../services/BescheidsService";
import { getBetriebDateiUploadEndpoint, getBetriebsDateienEndpoint } from "../../../services/BetriebeService";
import { getDateiDownloadDateiEndpoint, getDeleteDateiEndpoint } from "../../../services/DateiService";
import { handleApiException, useHttp } from "../../../services/HttpService";
import { createCalculatedColumn, createColumn, createComponentColumn, createDateColumn, NUMBERFORMAT } from "../../../utils/ColumnUtils";
import { userHasAnyRole } from "../../../utils/SecurityUtils";
import { NUMBER } from "../../../utils/URLUtils";
import usePathParam from "../../../utils/usePathParam";
import { confirmDeleteOption } from "../../ConfirmationDialog/confirmationDialogOptions";
import { useConfirmation } from "../../ConfirmationDialog/ConfirmationDialogProvider";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADownloadButton from "../../EADownloadButton";
import EAFileUploadDialog from "../../EAFileUploadDialog";
import CollapsibleTable from "../../tableComponents/CollapsibleTable";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import { useLocation } from "react-router-dom";

export default function DateienComponent({ modus, setToggleAllowed }) {
    const confirm = useConfirmation();
    const betriebsId = usePathParam("id", NUMBER);
    const anlagenId = usePathParam("anlageid", NUMBER);
    const bescheidsId = usePathParam("bescheidsid", NUMBER);
    const location = useLocation();

    const [uploadPath, setUploadPath] = useState();
    const [uploadDlgOpen, setFileUploadDlgOpen] = useState(false);

    const [refetchUrl, setRefetchUrl] = useState();
    const [files, refetchFiles] = useHttp(refetchUrl, { manual: true });
    const [{ loading: deleting }, deleteFile] = useHttp({ method: "DELETE" }, { manual: true });
    const [readonly, setReadonly] = useState(true);
    const [{
        data: bescheidData
    }, refetchBescheid] = useHttp(getBescheidEndpoint(bescheidsId), { manual: true });

    useEffect(() => {
        if (bescheidsId) refetchBescheid();
    }, [bescheidsId, refetchBescheid]);

    function deleteDatei(datei) {
        confirm(
            confirmDeleteOption(
                "die Datei",
                () =>
                    deleteFile({ url: getDeleteDateiEndpoint(datei.id) })
                        .then(() => {
                            refetchFiles().catch((e) => handleApiException(e, "Error refetching files after delete"));
                            toast.success("Die Datei wurde erfolgreich gelöscht.");
                        })
                        .catch((e) => handleApiException(e, "Error while trying to delete File")),
                () => {}
            )
        );
    }

    const fileColumns = [
        createComponentColumn(
            "bezeichnung",
            (row, index) => (
                <EADownloadButton
                    id={"downloadFile" + index}
                    label={row.bezeichnung}
                    url={getDateiDownloadDateiEndpoint(row.id)}
                    variant="text"
                    color="primary"
                    style={{ background: "transparent" }}
                />
            ),
            "Bezeichnung"
        ),
        createColumn("uploadVon", "Hochgeladen von"),
        createDateColumn("uploadAm", "Datum"),
        createCalculatedColumn(
            "dateigroesse",
            (row) => NUMBERFORMAT.format(parseFloat((row.dateigroesse / Math.pow(1024, 2)).toFixed(3))),
            "Größe [MB]"
        ),
        createCalculatedColumn("dateityp", (row) => row.dateityp.value, "Dateityp"),
    ];

    const fileColumnsWithDelete = [
        createComponentColumn("actionColumn", (row, index) => (
            <EATableActionButton id={"dateiDeleteAction-" + index} iconName="ico-delete">
                <span onClick={() => deleteDatei(row)} />
            </EATableActionButton>
        )),
        ...fileColumns,
    ];

    useEffect(() => {
        if (refetchUrl) {
            refetchFiles().catch((e) => handleApiException(e, "Error refetching files"));
        }
    }, [refetchUrl, refetchFiles]);

    useEffect(() => {
        if (betriebsId === undefined) {
            return;
        }
        setReadonly(modus !== "s");
        if (bescheidsId) {
            setRefetchUrl(getBescheidsDateienEndpoint(bescheidsId));
            setUploadPath(getBescheidsDateiUploadEndpoint(bescheidsId));
        } else if (anlagenId) {
            setRefetchUrl(getAnlagenDateienEndpoint(anlagenId));
            setUploadPath(getAnlagenDateiUploadEndpoint(anlagenId));
        } else if (betriebsId) {
            setRefetchUrl(getBetriebsDateienEndpoint(betriebsId));
            setUploadPath(getBetriebDateiUploadEndpoint(betriebsId));
        }
    }, [betriebsId, anlagenId, bescheidsId, modus, refetchFiles]);

    useEffect(() => {
        if (bescheidsId) {
            if (location.pathname.includes("/antrag/")) {
                setToggleAllowed(userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER]));
            } else {
                setToggleAllowed(userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]));
            }
        } else if (anlagenId || betriebsId) {
            setToggleAllowed(userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]));
        } else {
            setToggleAllowed(false);
        }
    }, [setToggleAllowed, bescheidsId, anlagenId, betriebsId, location.pathname]);

    function handleFileUploadSuccessed() {
        setFileUploadDlgOpen(false);
        refetchFiles().catch((e) => handleApiException(e, "Error refetching files after Upload"));
        toast.success("Die Datei wurde erfolgreich hochgeladen");
    }

    function handleFileUploadError(error) {
        toast.error(error ? error : "Die Datei konnte nicht hochgeladen werden!");
    }

    return (
        <div className="gridl tabsheet-content-margin" style={{ gridTemplateRows: "max-content min-content" }}>
            <EAFileUploadDialog
                open={uploadDlgOpen}
                testId="dateiUploadDlg"
                label="Datei hochladen"
                onClose={() => setFileUploadDlgOpen(false)}
                uploadPath={uploadPath}
                onSuccess={handleFileUploadSuccessed}
                onError={handleFileUploadError}
            />
            {
                <CollapsibleTable
                    id="dateien-table"
                    label="Dateien"
                    loading={files.loading || deleting}
                    columns={readonly ? fileColumns : fileColumnsWithDelete}
                    data={files.data}
                />
            }
            {!readonly && (!bescheidsId || (bescheidData && bescheidData.bescheidsstatus.id !== "FREIGEGEBEN")) && (
                <EAButtonBar>
                    <EAButton
                        id="openFileUploadDlgButton"
                        label="Datei hochladen"
                        onClick={() => setFileUploadDlgOpen(true)}
                        disabled={!uploadPath}
                        loading={!uploadPath}
                    />
                </EAButtonBar>
            )}
        </div>
    );
}
